import { useCallback, useEffect, useRef, useState } from 'react';
import { checkPickedInteractions } from '../../api/interactionChecker';
import { pollInteractionCheck } from '../../utils/interactionChecker';
import { logAnalyticsEvent } from '../../utils/analytics';
import { buildResultRows, isCompletedAnswer, pairsForAnalytics } from './resultRows';

// The check behind the checker page's Check button (STACK-587; design doc §3
// steps 4–6, §3.2).
//
// It posts exactly the picks and polls with `pollInteractionCheck` at its
// default cadence (10 retries, 10 s apart): the route's 50-a-day ceiling
// counts every poll and is sized on that cadence. The page sets no time limit
// of its own (Tanu, 2026-10-16); only the polls running out end a check.
// Every refusal, a poll timeout and any answer that is not a completed one
// become the one couldn't-check state, never a clean result.

export const CHECK_PHASE = {
  FORM: 'form',
  CHECKING: 'checking',
  RESULTS: 'results',
  COULDNT_CHECK: 'couldnt_check',
};

/** The analytics `reason` for a refusal. Every one renders the same message. */
export const refusalReason = (error) => {
  if (error?.timedOut) return 'timeout';
  switch (error?.status) {
    case 400: return 'bad_request';
    case 429:
      if (error.code === 'checker_limit_reached') return 'checker_limit_reached';
      return 'rate_limited';
    case 503:
      if (error.code === 'checker_ai_budget_spent') return 'budget_spent';
      if (error.code === 'name_lookup_failed') return 'name_lookup_failed';
      return 'server_error';
    case undefined:
    case null:
      return error?.reason || 'network_error';
    default: return 'server_error';
  }
};

// A 200 that is not a completed answer, or one listing a risk we cannot read.
const unusable = (response) => {
  const error = new Error('The check gave no usable answer');
  error.reason = isCompletedAnswer(response) ? 'unreadable_answer' : 'not_completed';
  return error;
};

/**
 * @param {Object} options
 * @param {Function} options.getIdToken - `() => Promise<string|null>`, the
 *   signed-in caller's ID token, or null for a visitor
 * @param {number} [options.retryDelayMs] - tests only
 * @param {number} [options.maxRetries] - tests only
 */
export default function useCheckerCheck({ getIdToken, retryDelayMs, maxRetries } = {}) {
  const [phase, setPhase] = useState(CHECK_PHASE.FORM);
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState({ medications: [], supplements: [] });
  const runRef = useRef(0);
  const controllerRef = useRef(null);

  // Leaving the page stops asking: every poll counts against the ceiling.
  useEffect(() => () => {
    runRef.current += 1;
    controllerRef.current?.abort();
  }, []);

  const run = useCallback(async (medications, supplements) => {
    if (!medications.length || !supplements.length) return;
    const run = ++runRef.current;
    const current = () => run === runRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    const rxcuis = medications.map((m) => m.in_rxcui);
    const keys = supplements.map((s) => s.key);
    setChecked({ medications, supplements });
    setRows([]);
    setPhase(CHECK_PHASE.CHECKING);

    const base = {
      medications: rxcuis,
      supplements: keys,
    };

    try {
      let idToken = null;
      try {
        idToken = getIdToken ? await getIdToken() : null;
      } catch (_) {
        idToken = null;
      }
      const response = await pollInteractionCheck(() => checkPickedInteractions(rxcuis, keys, idToken), {
        ...(retryDelayMs !== undefined && { retryDelayMs }),
        ...(maxRetries !== undefined && { maxRetries }),
        signal: controller.signal,
      });
      if (!current()) return;
      const built = buildResultRows(response, medications, supplements);
      if (!built) throw unusable(response);
      const pairs = pairsForAnalytics(built, medications);
      const flagCount = pairs.filter((p) => p.risk !== 'none').length;
      setRows(built);
      setPhase(CHECK_PHASE.RESULTS);
      // Keys, RxCUIs and risks only — never reasoning text, never a name (§5).
      logAnalyticsEvent('checker_check_result', {
        outcome: flagCount ? 'flags' : 'clean',
        reason: null,
        ...base,
        pairs,
        pair_count: pairs.length,
        flag_count: flagCount,
      });
    } catch (error) {
      if (!current() || error?.name === 'AbortError') return;
      setRows([]);
      setPhase(CHECK_PHASE.COULDNT_CHECK);
      logAnalyticsEvent('checker_check_result', {
        outcome: 'couldnt_check',
        reason: refusalReason(error),
        ...base,
        pairs: [],
        pair_count: rxcuis.length * keys.length,
        flag_count: 0,
      });
    }
  }, [getIdToken, retryDelayMs, maxRetries]);

  /** "Try again": the same lists, checked again. */
  const retry = useCallback(() => run(checked.medications, checked.supplements), [run, checked]);

  return { phase, rows, checked, run, retry };
}
